import type { AiApi, FindingFilter, FindingStatusWord, FindingView } from '@vitesse/base-ui/ai';

/*
 * FindingsManager — main-thread state for the Findings view: the org's findings log, one filtered
 * list at a time, read a page at a time, and a person's decisions on its proposals.
 *
 * The log is the AI plane's; every call goes through the AI worker's api (Cardinal Rule — components
 * never fetch). Nothing streams: an entry never changes, and a decision is either this person's
 * (applied here from the reply) or someone else's (seen the next time the list is loaded), so there
 * is nothing to poll for.
 *
 * ONE LIST, owned by whichever view is showing — the org page and a controller's tab are never on
 * screen together, and each loads its own filter when it opens. Full entries (code, history) are
 * cached by id until the list is loaded again: an entry changes only by a decision, and this
 * person's replaces it here, but someone else's is seen only when the log is read again — so the
 * cache goes with the list, or an entry opened before would keep its old status and buttons.
 */
type FindingsApi = Pick<AiApi, 'listFindings' | 'readFindings' | 'setFindingStatus'>;

/** The tabs a Findings view shows, counted. */
export interface FindingCounts {
  open: number;
  due: number;
  all: number;
}

export class FindingsManager extends EventTarget {
  private list: FindingView[] = [];
  private count = 0;
  private cursor = '';
  private current: FindingFilter = {};
  private busy = false;
  private errorMessage = '';
  private loadedOnce = false;
  private generation = 0;
  private readonly full = new Map<string, FindingView>();
  private tabCounts: FindingCounts | null = null;
  private countBase: FindingFilter = {};
  private countGeneration = 0;

  constructor(private readonly api: FindingsApi) {
    super();
  }

  /** The entries loaded so far for the current filter, newest first. */
  get items(): FindingView[] {
    return this.list;
  }

  /** How many entries match the current filter, loaded or not. */
  get total(): number {
    return this.count;
  }

  get filter(): FindingFilter {
    return this.current;
  }

  get loading(): boolean {
    return this.busy;
  }

  /** True once the current filter's first page has answered (with entries, none, or an error). */
  get ready(): boolean {
    return this.loadedOnce;
  }

  /** The plane's sentence for the last failed read ('' when it succeeded). */
  get error(): string {
    return this.errorMessage;
  }

  get hasMore(): boolean {
    return this.cursor !== '';
  }

  /** How many entries each tab holds under the last base filter; null until counted. */
  get counts(): FindingCounts | null {
    return this.tabCounts;
  }

  /**
   * Count the three tabs — open, due, all — under a base filter (the scope, the search, the relay).
   * One `limit: 1` read each: the plane counts what matches on a first page, so the number costs one
   * row. Asked when the base changes, not on every tab switch, and again after a decision.
   */
  async refreshCounts(base: FindingFilter): Promise<void> {
    const mine = ++this.countGeneration;
    this.countBase = base;
    const [open, due, all] = await Promise.all(
      [{ open: true }, { due: true }, {}].map((tab) => this.api.listFindings({ ...base, ...tab, limit: 1 })),
    );
    if (mine !== this.countGeneration) return;
    this.tabCounts = [open, due, all].some((p) => p!.error) ? null : { open: open!.total, due: due!.total, all: all!.total };
    this.changed();
  }

  /** Show this filter: the list is cleared and its first page read. A newer call wins over an older
   *  one still in flight, so switching tabs quickly never shows the tab you left. */
  async load(filter: FindingFilter): Promise<void> {
    const mine = ++this.generation;
    this.current = filter;
    this.list = [];
    this.count = 0;
    this.cursor = '';
    this.loadedOnce = false;
    this.full.clear();
    await this.fetchPage(mine);
  }

  /** The next page of the current filter, after the ones shown. */
  async more(): Promise<void> {
    if (!this.cursor || this.busy) return;
    await this.fetchPage(this.generation);
  }

  /** One entry in full — its evidence code and status history. Null when it cannot be read. */
  async read(id: string): Promise<FindingView | null> {
    return (await this.readMany([id])).get(id) ?? null;
  }

  /** Several entries in full, in one call for those not read yet — an entry's definition and the ones
   *  it follows. One that cannot be read is absent from the map. */
  async readMany(ids: readonly string[]): Promise<Map<string, FindingView>> {
    const missing = [...new Set(ids)].filter((id) => !this.full.has(id));
    if (missing.length) for (const f of await this.api.readFindings(missing)) this.full.set(f.id, f);
    const out = new Map<string, FindingView>();
    for (const id of ids) {
      const f = this.full.get(id);
      if (f) out.set(id, f);
    }
    return out;
  }

  /**
   * A person's decision on a proposal, with why. On success the entry the plane returns — with its
   * new history — replaces the one in the list and the cache. On failure nothing changes, and the
   * plane's sentence is returned for the dialog to show.
   */
  async decide(id: string, status: Exclude<FindingStatusWord, '' | 'proposed'>, reason: string): Promise<{ ok: boolean; message: string }> {
    const r = await this.api.setFindingStatus(id, status, reason);
    if (!r.ok) return { ok: false, message: r.message };
    if (r.finding) {
      this.full.set(id, r.finding);
      // The list holds entries without their code; the decided one keeps that shape.
      const stripped = { ...r.finding, evidence: r.finding.evidence.map((e) => ({ ...e, code: '' })), history: [] };
      this.list = this.list.map((f) => (f.id === id ? stripped : f));
    } else {
      this.full.delete(id);
    }
    this.changed();
    void this.refreshCounts(this.countBase); // a decision moves an entry out of Open, or into it
    return { ok: true, message: r.message };
  }

  private async fetchPage(mine: number): Promise<void> {
    this.busy = true;
    this.changed();
    const first = !this.cursor;
    const page = await this.api.listFindings(this.current, this.cursor);
    if (mine !== this.generation) return; // a newer load owns the list now
    this.busy = false;
    this.loadedOnce = true;
    this.errorMessage = page.error;
    if (!page.error) {
      this.list = [...this.list, ...page.items];
      if (first) this.count = page.total;
      this.cursor = page.nextCursor;
    }
    this.changed();
  }

  private changed(): void {
    this.dispatchEvent(new CustomEvent('change'));
  }
}
